<template>
  <div>
    <!-- header -->
    <v-row class="mx-2" no-gutters>
      <v-list class="v-list-form transparent" width="100%">
        <v-list-item>
          <v-list-item-title class="py-2 subtitle-2 text--secondary font-weight-medium">{{ typeLabel }}</v-list-item-title>
        </v-list-item>
        <v-list-item class="py-0">
          <v-list-item-title>Resumen</v-list-item-title>
          <v-list-item-subtitle style="white-space: normal;">{{ insight.summary || '—' }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item class="py-0">
          <v-list-item-title>Tarea</v-list-item-title>
          <v-list-item-subtitle>{{ taskTitle(insight.module_key) || '—' }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="insight.entity || insight.entity_type" class="py-0">
          <v-list-item-title>Entidad</v-list-item-title>
          <v-list-item-subtitle>
            <entity-label :entity="insight.entity" :entity-type="insight.entity_type" :entity-id="insight.entity_id" />
          </v-list-item-subtitle>
        </v-list-item>
        <v-list-item class="py-0">
          <v-list-item-title>Generado el</v-list-item-title>
          <v-list-item-subtitle>{{ insight.generated_at | dateTime }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="insight.expires_at" class="py-0">
          <v-list-item-title>Expira el</v-list-item-title>
          <v-list-item-subtitle>{{ insight.expires_at | dateTime }}</v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </v-row>

    <!-- type: list (priorized worklist) -->
    <template v-if="insight.insight_type === 'list' && candidates.length">
      <v-divider class="mx-2 my-4" />
      <v-row class="mx-2" no-gutters>
        <div class="d-block mb-2">
          <span class="subtitle-2 text--secondary font-weight-medium">Worklist priorizada</span>
          <span v-if="portfolioRecovery !== null" class="ml-2 caption text--secondary">
            Recuperación esperada: {{ portfolioRecovery | currency(listCurrency) }} {{ listCurrency }}
          </span>
        </div>
        <v-sheet outlined rounded width="100%">
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Entidad</th>
                  <th class="text-right">Monto</th>
                  <th class="text-right">Días</th>
                  <th class="text-left">Segmento</th>
                  <th class="text-right">Prob. pago</th>
                  <th class="text-right">Recup. esp.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in candidates" :key="`cand-${i}`">
                  <td class="text-left">
                    {{ c.entity_id || '—' }}
                    <v-icon v-if="c.legal_blocked" class="ml-1" color="red" size="14">mdi-gavel</v-icon>
                  </td>
                  <td class="text-right">{{ c.outstanding_amount | currency(c.currency) }} {{ c.currency }}</td>
                  <td class="text-right">{{ c.days_past_due != null ? c.days_past_due : '—' }}</td>
                  <td class="text-left">{{ c.risk_segment || '—' }}</td>
                  <td class="text-right">{{ formatPct(c.prob_pay_14d) }}</td>
                  <td class="text-right">{{ c.expected_recovery_14d | currency(c.currency) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-sheet>
      </v-row>
    </template>

    <!-- type: report (daily digest) -->
    <template v-else-if="insight.insight_type === 'report' && hasData(insight.output_payload)">
      <template v-for="(section, key) in reportSections">
        <template v-if="hasData(section)">
          <v-divider class="mx-2 my-4" :key="`d-${key}`" />
          <v-row class="mx-2" no-gutters :key="`s-${key}`">
            <div class="d-block mb-2">
              <span class="subtitle-2 text--secondary font-weight-medium">{{ reportSectionLabel(key) }}</span>
            </div>
            <v-sheet outlined rounded width="100%">
              <v-list class="v-list-form transparent py-0">
                <v-list-item v-for="(value, field) in section" :key="`${key}-${field}`" class="py-0">
                  <v-list-item-title>{{ humanize(field) }}</v-list-item-title>
                  <v-list-item-subtitle class="text-right">{{ formatValue(value) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-sheet>
          </v-row>
        </template>
      </template>
    </template>

    <!-- type: alert -->
    <template v-else-if="insight.insight_type === 'alert'">
      <v-divider class="mx-2 my-4" />
      <v-row class="mx-2" no-gutters>
        <v-sheet :color="severity.color" class="pa-3" rounded width="100%">
          <div class="d-flex align-start">
            <v-icon class="mr-2" :color="severity.textColor" size="20">{{ severity.icon }}</v-icon>
            <span class="body-2" :class="`${severity.textColor}--text`" style="white-space: normal;">{{ insight.summary }}</span>
          </div>
        </v-sheet>
      </v-row>
      <template v-if="hasData(insight.output_payload)">
        <v-row class="mx-2 mt-3" no-gutters>
          <v-sheet outlined rounded width="100%">
            <pre class="ma-2 agent-json">{{ pretty(insight.output_payload) }}</pre>
          </v-sheet>
        </v-row>
      </template>
    </template>

    <!-- type: readiness -->
    <template v-else-if="insight.insight_type === 'readiness'">
      <v-divider class="mx-2 my-4" />
      <v-row class="mx-2" no-gutters>
        <div class="d-block mb-2">
          <v-chip :color="isReady ? 'green lighten-5' : 'amber lighten-5'" :text-color="isReady ? 'green darken-2' : 'amber darken-4'" label small>
            <v-icon left small>{{ isReady ? 'mdi-check-circle-outline' : 'mdi-alert-outline' }}</v-icon>
            {{ isReady ? 'Listo para facturar' : 'No listo' }}
          </v-chip>
        </div>
        <template v-if="blockers.length">
          <v-sheet outlined rounded width="100%">
            <v-list class="transparent py-0">
              <v-list-item v-for="(b, i) in blockers" :key="`blk-${i}`" class="py-0">
                <v-list-item-icon class="mr-2"><v-icon color="red" size="18">mdi-close-circle-outline</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-2 text--primary" style="white-space: normal;">{{ blockerLabel(b) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-sheet>
        </template>
        <span v-else class="body-2 text--secondary">Sin bloqueos detectados.</span>
      </v-row>
    </template>

    <!-- type: explanation -->
    <template v-else-if="insight.insight_type === 'explanation'">
      <v-divider class="mx-2 my-4" />
      <v-row class="mx-2" no-gutters>
        <span class="body-2 text--primary" style="white-space: normal;">{{ explanationText }}</span>
      </v-row>
    </template>

    <!-- default / signal / triage / template / strategy: generic payload -->
    <template v-else-if="hasData(insight.output_payload)">
      <v-divider class="mx-2 my-4" />
      <v-row class="mx-2" no-gutters>
        <div class="d-block mb-2">
          <span class="subtitle-2 text--secondary font-weight-medium">Detalle</span>
        </div>
        <v-sheet outlined rounded width="100%">
          <pre class="ma-2 agent-json">{{ pretty(insight.output_payload) }}</pre>
        </v-sheet>
      </v-row>
    </template>
  </div>
</template>
<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'
import EntityLabel from '@/components/agents/EntityLabel'
import { agentInsightSeverity, agentInsightTypeLabel, agentCollectionsTasks, agentBillingTasks } from '@/collections/agents'

const ALL_TASKS = [...agentCollectionsTasks, ...agentBillingTasks]

export default {
  components: {
    EntityLabel
  },
  mixins: [
    GenericViewMixin
  ],
  props: {
    insight: {
      type: Object,
      required: true
    }
  },
  computed: {
    severity () {
      return agentInsightSeverity[this.insight.severity] || agentInsightSeverity.info
    },
    typeLabel () {
      return agentInsightTypeLabel[this.insight.insight_type] || 'Insight'
    },
    candidates () {
      const payload = this.insight.output_payload || {}
      return Array.isArray(payload.candidates) ? payload.candidates : []
    },
    listCurrency () {
      const first = this.candidates.find(c => c.currency)
      return first ? first.currency : ''
    },
    portfolioRecovery () {
      const payload = this.insight.output_payload || {}
      if (payload.cash_impact && payload.cash_impact.portfolio_expected_recovery_14d != null) {
        return payload.cash_impact.portfolio_expected_recovery_14d
      }
      return null
    },
    reportSections () {
      const payload = this.insight.output_payload || {}
      return {
        operational_summary: payload.operational_summary || {},
        approval_queue: payload.approval_queue || {},
        cash_and_risk: payload.cash_and_risk || {}
      }
    },
    isReady () {
      const payload = this.insight.output_payload || {}
      return payload.ready === true || payload.is_ready === true
    },
    blockers () {
      const payload = this.insight.output_payload || {}
      return Array.isArray(payload.blockers) ? payload.blockers : []
    },
    explanationText () {
      const payload = this.insight.output_payload || {}
      return payload.explanation || payload.text || this.insight.summary || '—'
    }
  },
  methods: {
    taskTitle (moduleKey) {
      const task = ALL_TASKS.find(t => t.task_key === moduleKey)
      return task ? task.title : moduleKey
    },
    reportSectionLabel (key) {
      const map = {
        operational_summary: 'Resumen operacional',
        approval_queue: 'Cola de aprobaciones',
        cash_and_risk: 'Caja y riesgo'
      }
      return map[key] || this.humanize(key)
    },
    humanize (field) {
      return String(field).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
    },
    blockerLabel (blocker) {
      if (typeof blocker === 'string') return blocker
      if (blocker && typeof blocker === 'object') {
        return blocker.message || blocker.reason || blocker.label || JSON.stringify(blocker)
      }
      return String(blocker)
    },
    formatPct (value) {
      if (value == null) return '—'
      return `${Math.round(value * 100)}%`
    },
    formatValue (value) {
      if (value == null) return '—'
      if (typeof value === 'object') return JSON.stringify(value)
      return value
    },
    pretty (obj) {
      if (!this.hasData(obj)) return ''
      return JSON.stringify(obj, null, 2)
    },
    hasData (obj) {
      return obj && typeof obj === 'object' && Object.keys(obj).length > 0
    }
  }
}
</script>
<style scoped>
.agent-json {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: #5A6072;
}
</style>
